import {
    Cluster,
    ClusterStatus,
    isSameCluster
} from "in_queue/types/clusterType";
import { InQueueSubscription } from "in_queue/types/projectType";

// Cluster statuses are set globally and can be overridden on a per-subscription
// basis.
export const clusterStatusFromSubscription = (
    cluster: Cluster,
    subscription?: InQueueSubscription
) => {
    return subscription?.status_override || cluster.status;
};

const isClusterViewable = (clusterStatus: ClusterStatus) => {
    return clusterStatus !== ClusterStatus.AllDisabled;
};

// Whether the user can view the cluster, a combination of the cluster status
// and their subscription status.
export const canUserViewCluster = (
    subscription: InQueueSubscription,
    cluster: Cluster
) => {
    // Future: Fetch subscription status as part of the clusters query to
    // remove this comparison. We're currently comparing every cluster
    // against every subscription.
    // linear.app/nira-energy/issue/NIRA-4406/reference-in-queue-clusters-in-other-schema
    if (!isSameCluster(subscription.clusterInfo, cluster)) {
        return false;
    }

    const clusterStatus = clusterStatusFromSubscription(cluster, subscription);

    return isClusterViewable(clusterStatus);
};

// Customer-facing label for each cluster status. The statuses describe what a
// user may do (view / run preview scenarios, view / run DP scenarios), so the
// labels name the stage the cluster is in and whether it is still open for
// scenario runs.
export const CLUSTER_STATUS_LABELS: Record<ClusterStatus, string> = {
    [ClusterStatus.PreviewMode]: "Preview Open",
    [ClusterStatus.PreviewButNoScenarios]: "Preview Open (View Only)",
    [ClusterStatus.DpMode]: "Decision Point Open",
    [ClusterStatus.OnlyDpMode]: "Decision Point Open",
    [ClusterStatus.AfterDp]: "Decision Point Closed",
    [ClusterStatus.AllDisabled]: "Inactive",
    [ClusterStatus.Hidden]: "Inactive"
};

// Statuses where the user can still run scenarios, so the label reads as a
// live state rather than a historical one.
const OPEN_STATUSES: ReadonlySet<ClusterStatus> = new Set([
    ClusterStatus.PreviewMode,
    ClusterStatus.DpMode,
    ClusterStatus.OnlyDpMode
]);

export const isOpenClusterStatus = (status: ClusterStatus): boolean =>
    OPEN_STATUSES.has(status);
